import React, { useState } from 'react';
import {
  Modal,
  Button,
  Input,
  Message,
  List,
  IconButton,
  Tooltip,
  Whisper,
  useToaster,
} from 'rsuite';
import TrashIcon from '@rsuite/icons/Trash';
import authFetch from '../../helpers/authFetch';

const SPACING = {
  modalBody: 16,
  folderItem: 12,
  folderNameInput: 12,
  buttonGap: 8,
  listGap: 8,
};

const FolderManagementModal = ({ open, onClose, folders = [], onFoldersChanged }) => {
  const [newFolderName, setNewFolderName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const toaster = useToaster();

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Folder name cannot be empty');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await authFetch('/folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newFolderName.trim() }),
      });

      if (response.ok) {
        setNewFolderName('');
        toaster.push(
          <Message type="success" showIcon closable>
            Folder created successfully
          </Message>,
          { placement: 'topCenter', duration: 2000 }
        );
        if (onFoldersChanged) {
          const updated = await authFetch('/folders');
          if (updated.ok) {
            const data = await updated.json();
            onFoldersChanged(data);
          }
        }
      } else if (response.status === 409) {
        setError('Folder with this name already exists');
      } else {
        setError('Failed to create folder');
      }
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleRenameFolder = async (folderId) => {
    if (!editingName.trim()) {
      setError('Folder name cannot be empty');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`/folders/${folderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editingName.trim() }),
      });

      if (response.ok) {
        setEditingId(null);
        setEditingName('');
        toaster.push(
          <Message type="success" showIcon closable>
            Folder renamed successfully
          </Message>,
          { placement: 'topCenter', duration: 2000 }
        );
        if (onFoldersChanged) {
          const updated = await authFetch('/folders');
          if (updated.ok) {
            const data = await updated.json();
            onFoldersChanged(data);
          }
        }
      } else if (response.status === 409) {
        setError('Another folder with this name already exists');
      } else {
        setError('Failed to rename folder');
      }
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Are you sure? Decks in this folder will be moved to unfoldered.')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`/folders/${folderId}`, {
        method: 'DELETE',
      });

      if (response.ok || response.status === 204) {
        toaster.push(
          <Message type="success" showIcon closable>
            Folder deleted successfully
          </Message>,
          { placement: 'topCenter', duration: 2000 }
        );
        if (onFoldersChanged) {
          const updated = await authFetch('/folders');
          if (updated.ok) {
            const data = await updated.json();
            onFoldersChanged(data);
          }
        }
      } else {
        setError('Failed to delete folder');
      }
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <Modal.Header>
        <Modal.Title>Manage Folders</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ paddingBottom: SPACING.modalBody }}>
        {error && (
          <Message type="error" showIcon style={{ marginBottom: SPACING.folderItem }}>
            {error}
          </Message>
        )}

        <div style={{ marginBottom: SPACING.folderItem }}>
          <h6 style={{ marginBottom: 8 }}>Create New Folder</h6>
          <div style={{ display: 'flex', gap: SPACING.buttonGap }}>
            <Input
              placeholder="Folder name"
              value={newFolderName}
              onChange={setNewFolderName}
              disabled={loading}
              onPressEnter={handleCreateFolder}
            />
            <Button
              appearance="primary"
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || loading}
              loading={loading}
            >
              Create
            </Button>
          </div>
        </div>

        <div>
          <h6 style={{ marginBottom: SPACING.folderNameInput }}>Your Folders</h6>
          {folders.length === 0 ? (
            <p style={{ color: '#999' }}>No folders created yet</p>
          ) : (
            <List
              bordered
              size="sm"
              style={{ marginBottom: SPACING.folderItem }}
            >
              {folders.map((folder) => (
                <List.Item
                  key={folder.id}
                  style={{
                    padding: SPACING.folderItem,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  {editingId === folder.id ? (
                    <div style={{ display: 'flex', gap: SPACING.buttonGap, flex: 1 }}>
                      <Input
                        value={editingName}
                        onChange={setEditingName}
                        disabled={loading}
                        autoFocus
                        size="sm"
                      />
                      <Button
                        size="sm"
                        appearance="primary"
                        onClick={() => handleRenameFolder(folder.id)}
                        disabled={!editingName.trim() || loading}
                        loading={loading}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        appearance="subtle"
                        onClick={() => setEditingId(null)}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <>
                      <span>{folder.name}</span>
                      <div style={{ display: 'flex', gap: SPACING.buttonGap }}>
                        <Button
                          size="xs"
                          appearance="subtle"
                          onClick={() => {
                            setEditingId(folder.id);
                            setEditingName(folder.name);
                          }}
                          disabled={loading}
                        >
                          Rename
                        </Button>
                        <Whisper
                          placement="top"
                          trigger="hover"
                          speaker={<Tooltip>Delete folder</Tooltip>}
                        >
                          <IconButton
                            size="xs"
                            icon={<TrashIcon />}
                            appearance="ghost"
                            color="red"
                            onClick={() => handleDeleteFolder(folder.id)}
                            disabled={loading}
                          />
                        </Whisper>
                      </div>
                    </>
                  )}
                </List.Item>
              ))}
            </List>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} appearance="subtle">
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FolderManagementModal;
